// The user guide at /manual. Open to anyone, no sign-in needed.
// Text lives here as plain data, so a Bangla version can be added later.
import type { ReactNode } from 'react';
import { BrandMark } from '../ui/components';

type Section = { id: string; title: string; body: ReactNode };

const FUNCTIONS: { icon: string; name: string; what: string }[] = [
  { icon: '💊', name: 'Purchase calculator', what: 'Works out how many of each medicine to buy for any date range, after counting what you already have.' },
  { icon: '🏷️', name: 'Box labels and prescription print', what: 'Prints labels for medicine boxes (morning / lunch / dinner) and a full prescription sheet.' },
  { icon: '🧾', name: 'Pharmacy order', what: 'Exports the order as a PDF, an image or a price-calculation list to send to the pharmacy.' },
  { icon: '📷', name: 'Count pills from a photo', what: 'Take a photo of a strip or pile and tap each pill to count it.' },
  { icon: '⏰', name: 'Medicine reminders', what: 'Injections and other repeating doses on a calendar, with reminders before each one.' },
  { icon: '🩺', name: 'Doctor visits', what: 'Your doctors, upcoming visits, repeat visits, reminders and follow-ups, with calendar export.' },
  { icon: '🧪', name: 'Tests and results', what: 'Book tests, link them to a visit, enter results and see trends over time.' },
  { icon: '📚', name: 'Medicine library', what: 'Your saved medicine names and strengths, so adding a medicine takes seconds.' },
  { icon: '👨‍👩‍👧', name: 'Family and sharing', what: 'Track several people. Invite family members to view or help, each with the right access.' },
  { icon: '🔄', name: 'Works everywhere', what: 'The same data on your phone and computer, updated live, and it keeps working offline.' },
  { icon: '💾', name: 'Backups', what: 'Download everything as a file at any time, and bring it back if you ever need to.' },
];

const WHY: { title: string; text: string }[] = [
  { title: 'Never miss a dose or injection', text: 'Reminders arrive before each dose, and the calendar shows what was done and what was missed.' },
  { title: 'Buy exactly what you need', text: 'No more running out halfway through the month, and no more paying for boxes that expire on the shelf.' },
  { title: 'Everything for the doctor in one place', text: 'Medicines, visit history and test results are ready to show at every appointment.' },
  { title: 'The whole family can help', text: 'Share the work of caring for a parent or child, so everyone sees the same, up-to-date list.' },
  { title: 'Less paperwork', text: 'Labels, prescriptions and pharmacy orders are printed or shared in one click.' },
  { title: 'Private and safe', text: 'Only the people you invite can see your family’s information.' },
];

const SECTIONS: Section[] = [
  {
    id: 'start', title: 'Getting started',
    body: <>
      <ol>
        <li><b>Set your password.</b> Open the link in the invite email from your Bhorosha admin. Choose a password (at least 8 characters) and type it twice. The link works <b>once</b> and only for <b>24 hours</b>. If it has expired, ask the admin for a new one.
          <br />Got a <b>one-time password</b> from the admin instead? Sign in with your email and that password, then choose your own password. It works for 24 hours.</li>
        <li><b>Sign in</b> next time with your email and that password. Forgot it? Use <i>Forgot password?</i> on the sign-in page.</li>
        <li><b>Install it on your phone</b> so it opens like an app.
          <ul><li>Android (Chrome): menu ⋮ → <i>Install app</i>.</li><li>iPhone (Safari): Share → <i>Add to Home Screen</i>.</li></ul></li>
        <li>The first time, you either <b>set up your family</b> (give it a name and add the first person, or start from a backup file) or, if someone invited you, you go straight into their family.</li>
      </ol>
    </>,
  },
  {
    id: 'family', title: 'Your family and the people in it',
    body: <>
      <p>A <b>family</b> holds everyone whose medicines you look after. Each <b>person</b> has their own medicines, reminders, visits and tests.</p>
      <ul>
        <li><b>Switch person:</b> tap the name button at the top, then choose a person. Use ✏️ to edit their name, age, gender and dialysis days.</li>
        <li><b>Add a person:</b> name button → <i>+ Add person</i>.</li>
        <li><b>Family &amp; members:</b> tap your family button (top right). Here the owner can:
          <ul><li>invite people (<b>Can edit</b> or <b>Can view</b>, and choose which people they can see),</li><li>change or remove members,</li><li>set each person's relation to the owner (mother, father, son…).</li></ul></li>
        <li>The dot on the family button shows whether your changes are saved: <b>green</b> means saved, <b>yellow</b> means saving, <b>red</b> means offline and saved on your device until you're back online.</li>
      </ul>
    </>,
  },
  {
    id: 'calc', title: 'Purchase calculator',
    body: <>
      <ol>
        <li><b>Choose the dates.</b> Tap the <i>From → To</i> box at the top and pick the days you are buying for. The quick picks are <i>to the end of this month</i>, <i>to the end of next month</i>, and the next 30, 60 or 90 days. The ‹ › arrows move the end date by a month.</li>
        <li><b>Add each medicine.</b> Tap <i>+ Add Medicine</i>, start typing the name (your library suggests it), and choose how it is taken:
          <ul><li><b>Daily:</b> how many before or after breakfast, lunch and dinner. ½ and 1½ are allowed.</li><li><b>Dialysis:</b> per session, times per week.</li><li><b>Every few days:</b> for example an injection every 7 days.</li><li><b>Fixed amount:</b> for example 1 bottle lasts 30 days.</li></ul>
          You can also set a start date and how many days to take it, and tick <i>do not take on dialysis day</i>.</li>
        <li><b>Enter what you have.</b> Tap the <b>Have</b> number and type the count, or use <i>Count Pills from Photo</i>.</li>
        <li><b>Read the result.</b> <b>Need</b> is how many the dates require, <b>To Buy</b> is Need minus Have, and <b>Order Qty</b> is rounded up to pharmacy pack sizes. Tap Order Qty to change it for this date range; a <b>*</b> shows you changed it.</li>
      </ol>
      <p>The cards on the left (or under the list on a phone) show the next injection, doctor visit and test.</p>
    </>,
  },
  {
    id: 'print', title: 'Prints and pharmacy order',
    body: <ul>
      <li><b>Print → Box Labels:</b> one label per medicine, showing when to take it.</li>
      <li><b>Print → Full Prescription:</b> every medicine with its dose (for example 1+0+1) on one A4 page.</li>
      <li><b>Export for Pharmacy:</b> a PDF, an image you can send by WhatsApp, or a calculation list with blank price boxes.</li>
    </ul>,
  },
  {
    id: 'remind', title: 'Medicine reminders',
    body: <ul>
      <li><b>+ Add Reminder</b> for any medicine you take every few days, such as an injection. Set the dose, first date, time, how often, and how early to be reminded.</li>
      <li>The month list shows <b>every dose this month</b> with its status: Completed, Upcoming, Missed or Skipped. Use ‹ › to see other months.</li>
      <li>Tap a dose to <b>mark it completed or missed</b>, or to edit the reminder. Use <i>Skip Next</i> or <i>Pause</i> when needed.</li>
      <li><b>Mark every dose.</b> Once a dose's time has passed it shows <b>Not marked yet</b> (in red) until you mark it done, missed or skipped. Until then, the later doses of that medicine are <b>on hold</b>: no countdown and no alerts for them. You're reminded once a day about a dose that isn't marked. Marking late doesn't move the schedule.</li>
      <li>To get notifications, tap <i>Enable</i> on the notifications pill and allow them. Keep the app open or installed on your phone.</li>
    </ul>,
  },
  {
    id: 'visits', title: 'Doctor visits',
    body: <ul>
      <li><b>Doctors tab:</b> add each doctor once, with specialty, clinic, phone and how often you usually see them.</li>
      <li><b>+ Schedule Visit:</b> date, time, reason and what to bring. Add reminders (for example the day before at 9:00), and repeat every few weeks or months if needed.</li>
      <li>After the visit, tap <b>✓ Completed</b>, write the outcome and book the follow-up in one step.</li>
      <li><b>Add to Calendar</b> puts visits in your phone's calendar. <b>Print Upcoming</b> gives you a list to keep.</li>
    </ul>,
  },
  {
    id: 'tests', title: 'Tests and results',
    body: <ul>
      <li><b>+ Schedule Test:</b> choose one or more tests (CBC, HbA1c and so on), the lab and any preparation, and link it to a doctor visit. You can set it a few days before the visit automatically.</li>
      <li>When results come, tap <b>Enter Results</b>. Values outside the normal range are marked <b>H</b> (high) or <b>L</b> (low).</li>
      <li><b>Results &amp; Trends</b> shows each test over time with its normal range.</li>
      <li><b>Test Library</b> holds the tests you use, with units and normal ranges. You can add your own.</li>
    </ul>,
  },
  {
    id: 'vaccines', title: 'Vaccines',
    body: <ul>
      <li>Add the person's <b>date of birth</b> (✏️ next to their name) and the <b>Vaccines</b> tab shows each vaccine with its due date: the Bangladesh EPI schedule for children (BCG, Pentavalent, OPV, PCV, fIPV, MR, TCV, HPV) and Td for women aged 15–49.</li>
      <li>Tap <b>✓ Given</b> when a dose is done, or <b>⋯</b> to enter another date, plan a date, or mark it not needed. Later doses move to match the date you enter.</li>
      <li>Adult vaccines (flu, pneumococcal, hepatitis B, shingles…) are under <b>More vaccines you can add</b>. Add them when your doctor recommends them. <b>+ Add other vaccine</b> is for anything else.</li>
      <li>The schedule is a reminder tool, not medical advice. Always follow your EPI centre or doctor.</li>
    </ul>,
  },
  {
    id: 'bell', title: 'Reminders bell',
    body: <ul>
      <li>The <b>🔔</b> at the top lists everything waiting for you: doses not marked yet, doses due today, visits and tests in the next 7 days, and vaccines that are due or overdue. The red number means something new.</li>
      <li>It covers everyone you look after, not only the person selected. Tap an item to go straight to it.</li>
      <li>To choose who gets reminders for a person, edit the person and tick names under <b>Who gets reminders</b>. For example, tick yourself and your mother so you both see her reminders. She needs her own Bhorosha login, invited to the family with access to her own record.</li>
      <li>With <b>Alerts on</b>, you also get pop-up alerts for the other people you look after, not only the person selected.</li>
    </ul>,
  },
  {
    id: 'phone', title: 'Phone app for the family',
    body: <ul>
      <li>An older parent can use the <b>Bhorosha phone app</b> with only a <b>user name and password</b>. No email is needed. The family owner makes it in <b>Family &amp; members → Phone logins</b> and prints or shares the login card.</li>
      <li>The phone rings at each medicine time. They tap <b>✓ Taken</b> or <b>Skip</b>, and the medicine in hand goes down by the dose. It works without internet and catches up later.</li>
      <li>It also reminds them to check their <b>blood pressure</b>. Very high or low readings tell the carer.</li>
      <li>The family <b>owner</b> sets the reminder times for the whole family (breakfast, lunch, dinner, blood pressure) in <b>Family &amp; members → Family → Phone reminder times</b>. The owner also sets the <b>blood pressure levels</b> there (High, Very high, Low; Normal is in between). The carer's phone number is in <b>Edit person → Phone app</b>.</li>
    </ul>,
  },
  {
    id: 'dailylog', title: 'Daily log and blood pressure',
    body: <ul>
      <li><b>📒 Daily log</b> shows, for any day, what was due and what happened: taken at what time, skipped and why, not marked, or missed. It also shows who marked it and where (on the phone, from the reminder, or on the website).</li>
      <li>It also shows blood pressure readings, the medicine in hand, and each phone's health: last sync and whether alerts are allowed. <b>Week</b> and <b>Month</b> show a coloured grid and the share of doses taken.</li>
      <li>Gave the medicine yourself? Tap <b>Correct</b> to mark it, up to 7 days back. The change is saved with your name.</li>
      <li><b>🖨 Print</b> or <b>⬇ CSV</b> to take it to the doctor.</li>
      <li><b>❤️ Blood pressure</b> shows the readings and a chart, and lets you add a reading.</li>
    </ul>,
  },
  {
    id: 'library', title: 'Medicine library and types',
    body: <ul>
      <li>Save a medicine's name and strength once, and it is suggested every time you add it. You can also import a list from a CSV file.</li>
      <li><b>Medicine types</b> (tablet, injection, syrup…) set the icon, unit and pack size for rounding the order.</li>
    </ul>,
  },
  {
    id: 'backup', title: 'Backups, privacy and help',
    body: <ul>
      <li>Your data is saved online automatically, so you can use any device. Family owners can also download everything with 💾 → <i>Export everything</i> and keep the file somewhere safe.</li>
      <li>Only people invited to your family can see its information. Viewers can look but not change anything.</li>
      <li><b>Can't sign in?</b> Check the email address, then use <i>Forgot password?</i>. If your account says it is deactivated, or your link has expired, contact your Bhorosha admin.</li>
      <li><b>Something looks out of date?</b> Pull down or reload the page. Changes from other devices appear within a few seconds.</li>
    </ul>,
  },
];

export function ManualPage() {
  return (
    <div className="bh-manual">
      <header className="bh-manual-hero">
        <a href="/" className="bh-manual-brand"><BrandMark size={38} /> Bhorosha</a>
        <h1>User guide</h1>
        <p>Medicines, reminders, doctor visits and tests for your whole family, in one place.</p>
        <a className="bh-btn bh-btn-primary" href="/">Open Bhorosha</a>
      </header>

      <main className="bh-manual-body">
        <nav className="bh-manual-toc" aria-label="Contents">
          <b>Contents</b>
          <a href="#functions">What Bhorosha does</a>
          <a href="#why">Why you need it</a>
          {SECTIONS.map(s => <a key={s.id} href={`#${s.id}`}>{s.title}</a>)}
        </nav>

        <article>
          <section id="functions">
            <h2>What Bhorosha does</h2>
            <div className="bh-manual-grid">
              {FUNCTIONS.map(f => (
                <div className="bh-manual-fn" key={f.name}><span aria-hidden="true">{f.icon}</span><div><b>{f.name}</b><p>{f.what}</p></div></div>
              ))}
            </div>
          </section>

          <section id="why">
            <h2>Why you need it</h2>
            <div className="bh-manual-why">
              {WHY.map(w => <div key={w.title}><b>{w.title}</b><p>{w.text}</p></div>)}
            </div>
          </section>

          {SECTIONS.map((s, i) => (
            <section id={s.id} key={s.id}>
              <h2><span className="bh-manual-num">{i + 1}</span>{s.title}</h2>
              {s.body}
            </section>
          ))}
          <p className="bh-manual-foot">Questions? Contact your Bhorosha admin. · <a href="#functions">Back to top</a></p>
        </article>
      </main>
    </div>
  );
}
